/* eslint-disable no-shadow */

import { Base } from "..";
import Subscription from "../../types/Subscription";

const listeners = new Set<() => void>();

/**
 * Справочник транспорта целиком (категория → марки → модели → кузова)
 * и обновление списков справочника без подписок на сервере.
 */
class CarDirectory extends Base {
	public static async import(
		tree: CarDirectory.Tree,
	): Promise<CarDirectory.ImportResult> {
		const result = await this.request(
			(prpc) => prpc.theirsModel.carDirectory.import(tree),
			{ silent: false },
		);

		this.notifyChange();

		return result;
	}

	public static async export(): Promise<CarDirectory.Tree> {
		return this.request((prpc) => prpc.theirsModel.carDirectory.export(), {
			silent: false,
		});
	}

	/** Сообщить открытым спискам справочника, что данные изменились. */
	public static notifyChange() {
		listeners.forEach((listener) => listener());
	}

	/**
	 * «Подписка» на список справочника через обычный getAll: первый запрос
	 * сразу, повторный - при смене параметров и после notifyChange().
	 * Интерфейс как у прежних подписок, поэтому useModelSubscribe не меняется.
	 */
	public static requestSubscription<Options, Model>(
		getAll: (
			options: Options,
		) => Promise<CarDirectory.GetAllResult | null> | null,
		fromResponse: (data: any) => Model,
		initialOptions: Options,
		onUpdate: Subscription.OnUpdate<Model>,
	): Subscription<Options> {
		let options = initialOptions;
		let active = true;
		let requestId = 0;

		const load = async () => {
			const currentRequestId = ++requestId;
			const response = await getAll(options);

			// Ответ на устаревшие параметры или после отписки не нужен.
			if (!active || currentRequestId !== requestId) return;
			// Сессии ещё нет (страница открыта до авторизации): список
			// перезапросится по notifyChange() из Session после входа.
			if (!response) return;

			await onUpdate({
				models: (response.items ?? []).map(fromResponse),
				metadataState: { total: response.pagination?.count ?? 0 },
			});
		};

		listeners.add(load);
		load();

		return {
			unsubscribe: async () => {
				active = false;
				listeners.delete(load);
			},
			// Не ждём загрузку: useModelSubscribe после update() сбрасывает
			// loading из старого снимка данных и затёр бы пришедший список.
			// Данные, как и у подписки, приходят отдельно через onUpdate.
			update: async (newOptions: Options) => {
				options = newOptions;
				load();
			},
		};
	}
}

declare namespace CarDirectory {
	type Name = string | Record<string, string>;

	interface Model {
		name: string;
		bodyTypes: Name[];
	}

	interface Brand {
		name: string;
		models: Model[];
	}

	interface Category {
		name: Name;
		brands: Brand[];
	}

	type Tree = Category[];

	interface Counter {
		created: number;
		restored: number;
		existing: number;
	}

	interface ImportResult {
		categories: Counter;
		brands: Counter;
		models: Counter;
		bodyTypes: Counter;
	}

	interface GetAllResult {
		items: any[];
		pagination?: { count?: number };
	}
}

export default CarDirectory;
